import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { WhyUs } from '../components/WhyUs';
import { CTA } from '../components/CTA';
import { Footer } from '../components/Footer';
import { Target, Users, Shield, CheckCircle2 } from 'lucide-react';

const values = [
  {
    icon: Target,
    title: 'Praxisorientiert',
    description: 'Wir kennen den Alltag im Bildungsträger aus eigener Erfahrung und bieten Lösungen, die in der Praxis funktionieren.'
  },
  {
    icon: Users,
    title: 'Partnerschaftlich',
    description: 'Wir arbeiten auf Augenhöhe und begleiten Sie durch den gesamten Prozess – klar, direkt und ohne Umwege.'
  },
  {
    icon: Shield,
    title: 'Auditsicher',
    description: 'Unser Ziel ist Ihre erfolgreiche Zulassung oder ein bestandenes Audit ohne Abweichungen.'
  },
  {
    icon: CheckCircle2,
    title: 'Verbindlich',
    description: 'Klare Absprachen, feste Ansprechpartner und verlässliche Reaktionszeiten.'
  }
];

const UeberUns = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="ueber-uns-page">
      <Navbar />
      {/* Hero glow that extends behind navbar */}
      <div className="hero-glow" />
      <main className="pt-20 relative">
        {/* Header */}
        <section className="py-24 md:py-32 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto"
            >
              <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                Über uns
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold tracking-tight leading-none mb-6" data-testid="ueber-uns-headline">
                Beratung aus der{' '}
                <span className="text-primary">Praxis</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Wir wissen, wie Bildungsträger arbeiten – weil wir selbst aus der Branche kommen.
              </p>
            </motion.div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-24 md:py-32 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6">
                  Erfahrung trifft Struktur
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    MEHRAZAV entstand aus der praktischen Arbeit mit Bildungsträgern. Wir kennen die Anforderungen der AZAV nicht nur aus Handbüchern, sondern aus dem täglichen Betrieb.
                  </p>
                  <p>
                    Unsere Beratung ist sachlich, strukturiert und auf Ergebnisse ausgerichtet. Keine Marketing-Floskeln, keine übertriebenen Versprechen – sondern konkrete Hilfe bei Zulassung, Audit und Qualitätsmanagement.
                  </p>
                  <p>
                    Ob Sie einen neuen Träger gründen, vor einem Audit stehen oder laufende QM-Unterstützung benötigen: Wir begleiten Sie mit klarer Kommunikation und praxiserprobten Lösungen.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-card/30 border border-white/10 rounded-2xl p-8">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-6 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="text-3xl font-heading font-bold text-primary mb-2">50+</div>
                      <div className="text-sm text-muted-foreground">Begleitete Träger</div>
                    </div>
                    <div className="text-center p-6 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="text-3xl font-heading font-bold text-primary mb-2">0</div>
                      <div className="text-sm text-muted-foreground">Abweichungen</div>
                    </div>
                    <div className="text-center p-6 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="text-3xl font-heading font-bold text-primary mb-2">5+</div>
                      <div className="text-sm text-muted-foreground">Jahre Erfahrung</div>
                    </div>
                    <div className="text-center p-6 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="text-3xl font-heading font-bold text-primary mb-2">24h</div>
                      <div className="text-sm text-muted-foreground">Reaktionszeit</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                Unsere Werte
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6">
                Wie wir arbeiten
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card/30 border border-white/10 rounded-2xl p-8 hover:border-primary/30 transition-all"
                  data-testid={`value-card-${index}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-white mb-3">{value.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default UeberUns;
